import type { Logger } from '@core/logging/logger.ts'
import type { HtmlPrompt } from '../../types/prompts.ts'
import { Env } from '../../env.ts'
import { getRandomString } from '@core/utils/random.ts'
import {
    type AnimationConfig,
    type AnimationCoordinator,
    type AnimationKeyframes,
    type AnimationTransform,
    type EnabledAnimationConfig,
    AnimationStage,
    AnimationType,
} from './animation-types.ts'
import { animationTransformToStr, getBaseAnimationTransform, strToAnimationTransform } from './animation-utilities.ts'

/** @internal */
export abstract class AbstractAnimationCoordinator implements AnimationCoordinator {
    public static get ANIMATION_PREFIX() {
        return 'pnax' as const
    }

    protected currentOrientation: string | undefined

    protected constructor(
        protected readonly prompt: HtmlPrompt,
        protected readonly logger: Logger,
    ) {
        if (this._screen?.orientation) {
            this.currentOrientation = this._screen.orientation.type
            this._screen.orientation.addEventListener('change', this._onOrientationChange.bind(this))
        }
    }

    protected get _screen(): Screen | null {
        return 'screen' in Env._scope ? Env._scope.screen : null
    }

    protected get _document(): Document {
        return Env._document
    }

    protected get _promptPackageId(): string {
        return `push-sdk-prompt-${this.prompt.id}`
    }

    protected get _promptPackageElement(): HTMLElement | null {
        const pkg = this._document.getElementById(this._promptPackageId)
        if (!pkg) {
            this.logger.error('Unable to find injected prompt element')
            return null
        }

        return pkg
    }

    protected get _promptElement(): HTMLElement | null {
        const pkg = this._promptPackageElement
        if (!pkg) {
            return null
        }

        const container = pkg.querySelector('.push-sdk-prompt-container')
        if (!container) {
            this.logger.error('Unable to find prompt element')
            return null
        }

        return container as HTMLElement
    }

    protected get _currentAnimationTransformConfig(): {
        curr: AnimationTransform
        start: AnimationTransform
        end: AnimationTransform
    } {
        const promptElement = this._promptElement

        const curr = promptElement
            ? strToAnimationTransform(getComputedStyle(promptElement).transform)
            : getBaseAnimationTransform()

        return {
            curr,
            start: Object.assign(Object.create(null), curr),
            end: Object.assign(Object.create(null), curr),
        }
    }

    protected get _entryAnimationConfig(): AnimationConfig {
        return (
            this.prompt.behavior?.display_animations?.enter ??
            ({
                enabled: true,
                animations: [AnimationType.FADE],
                duration_milliseconds: 420,
            } satisfies AnimationConfig)
        )
    }

    protected get _exitAnimationConfig(): AnimationConfig {
        return (
            this.prompt.behavior?.display_animations?.exit ??
            ({
                enabled: true,
                animations: [AnimationType.FADE],
                duration_milliseconds: 420,
            } satisfies AnimationConfig)
        )
    }

    protected _generateAnimationId(): string {
        return `${AbstractAnimationCoordinator.ANIMATION_PREFIX}_${this.prompt.id}_${getRandomString(5)}`
    }

    public _animateIn(config: AnimationConfig, onComplete?: () => void) {
        this._applyAnimation(AnimationStage.ENTRY, config, onComplete)
    }

    public _animateOut(config: AnimationConfig, onComplete?: () => void) {
        this._applyAnimation(AnimationStage.EXIT, config, onComplete)
    }

    private _onOrientationChange() {
        if (!this._screen?.orientation) {
            return
        }

        const prevOrientation = this.currentOrientation!
        const nextOrientation = this._screen.orientation.type
        this.currentOrientation = this._screen.orientation.type

        this._handleOrientationChange(nextOrientation, prevOrientation)
    }

    protected abstract _handleOrientationChange(nextOrientation: string, prevOrientation: string): void

    protected abstract _applyAnimation(stage: AnimationStage, config: AnimationConfig, onComplete?: () => void): void

    protected _getAnimationSteps(
        stage: AnimationStage,
        config: EnabledAnimationConfig,
        containerRect: DOMRect,
    ): AnimationKeyframes {
        const keyframes: AnimationKeyframes = {
            from: {},
            to: {},
        }

        const transformations = this._currentAnimationTransformConfig
        const willChangeProps = new Set<string>()

        /** FADE */
        const fadeMin = 0
        const fadeMax = 1

        if (config.animations.includes(AnimationType.FADE)) {
            willChangeProps.add('opacity')
            keyframes.from.opacity = stage === AnimationStage.ENTRY ? fadeMin : fadeMax
            keyframes.to.opacity = stage === AnimationStage.ENTRY ? fadeMax : fadeMin
        }

        /** ZOOM */
        const scaleMin = 0
        const scaleMax = 2

        if (config.animations.includes(AnimationType.ZOOM_IN)) {
            willChangeProps.add('transform')

            if (stage === AnimationStage.ENTRY) {
                transformations.start.scaleX = scaleMin
                transformations.start.scaleY = scaleMin
            } else {
                transformations.end.scaleX = scaleMax
                transformations.end.scaleY = scaleMax
            }
        }
        if (config.animations.includes(AnimationType.ZOOM_OUT)) {
            willChangeProps.add('transform')

            if (stage === AnimationStage.ENTRY) {
                transformations.start.scaleX = scaleMax
                transformations.start.scaleY = scaleMax
            } else {
                transformations.end.scaleX = scaleMin
                transformations.end.scaleY = scaleMin
            }
        }

        /** SLIDE */
        const offsetHeight = containerRect.height / 2
        const offsetWidth = containerRect.width / 2

        if (config.animations.includes(AnimationType.SLIDE_DOWN)) {
            willChangeProps.add('transform')

            if (stage === AnimationStage.ENTRY) {
                transformations.start.translateY -= offsetHeight
            } else {
                transformations.end.translateY += offsetHeight
            }
        }
        if (config.animations.includes(AnimationType.SLIDE_UP)) {
            willChangeProps.add('transform')

            if (stage === AnimationStage.ENTRY) {
                transformations.start.translateY += offsetHeight
            } else {
                transformations.end.translateY -= offsetHeight
            }
        }
        if (config.animations.includes(AnimationType.SLIDE_RIGHT)) {
            willChangeProps.add('transform')

            if (stage === AnimationStage.ENTRY) {
                transformations.start.translateX -= offsetWidth
            } else {
                transformations.end.translateX += offsetWidth
            }
        }
        if (config.animations.includes(AnimationType.SLIDE_LEFT)) {
            willChangeProps.add('transform')

            if (stage === AnimationStage.ENTRY) {
                transformations.start.translateX += offsetWidth
            } else {
                transformations.end.translateX -= offsetWidth
            }
        }

        if (willChangeProps.size) {
            keyframes.from.willChange = Array.from(willChangeProps).join(',')
            keyframes.to.willChange = Array.from(willChangeProps).join(',')
        }

        keyframes.from.transform = animationTransformToStr(transformations.start)
        keyframes.to.transform = animationTransformToStr(transformations.end)

        return keyframes
    }
}
